import { Injectable } from '@angular/core';
import { Headers, RequestOptions, Http, Response } from '@angular/http';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/throw';

export interface PaymentRecord {
  transactionId: string;
  statusCode: string;
  status?: string;
  amount?: string;
  bankTxnId?: string;
  paymentMode?: string;
  statusMessage?: string;
  dateTime?: string;
  customerEmail?: string;
  customerMobile?: string;
  rawResponse?: any;
  storedAt: string;
}

@Injectable()
export class PaymentService {

  headers: Headers;
  options: RequestOptions;
  feedbackApiUrl = 'https://needly.in/web_Services/NeedlyService.svc/UploadFeedbackDetails/Feedback';
  enquiryApiUrl = 'https://needly.in/web_Services/NeedlyService.svc/UploadEnquiryDetails/Enquiry';

  // In-memory storage for payment records (use IndexedDB or backend in production)
  private payments = new Map<string, PaymentRecord>();

  // Subject for payment status updates
  private paymentStatusSubject = new Subject<PaymentRecord | null>();

  constructor(private _http: Http) {
    this.headers = new Headers({ 'Content-Type': 'text/plain' });
    this.options = new RequestOptions({ headers: this.headers });

    // Load payments from localStorage on init
    this.loadPaymentsFromStorage();
  }

  /**
   * Load payments from localStorage
   */
  private loadPaymentsFromStorage(): void {
    try {
      const stored = localStorage.getItem('payment_records');
      if (stored) {
        const records: PaymentRecord[] = JSON.parse(stored);
        records.forEach(record => {
          this.payments.set(record.transactionId, record);
        });
        console.log('📦 Loaded ' + records.length + ' payment records from storage');
      }
    } catch (error) {
      console.error('Error loading payments from storage:', error);
    }
  }

  /**
   * Save payments to localStorage
   */
  private savePaymentsToStorage(): void {
    try {
      const records = Array.from(this.payments.values());
      localStorage.setItem('payment_records', JSON.stringify(records));
      console.log('💾 Saved ' + records.length + ' payment records to storage');
    } catch (error) {
      console.error('Error saving payments to storage:', error);
    }
  }

  /**
   * Store payment callback data (handles multiple response formats)
   */
  storePaymentCallback(paymentData: any): void {
    console.log('💾 Storing payment callback:', paymentData);

    let txnId = '';
    let statusCode = '';
    let status = '';
    let amount = '';
    let bankTxnId = '';
    let paymentMode = '';
    let statusMessage = '';
    let dateTime = '';
    let customerEmail = '';
    let customerMobile = '';

    // Handle pipe-delimited format (most common from Worldline POST)
    if (paymentData.msg && typeof paymentData.msg === 'string') {
      const parsed = this.parseWorldlineMsg(paymentData.msg);
      txnId = parsed.txnId;
      statusCode = parsed.statusCode;
      status = parsed.status;
      amount = parsed.amount;
      bankTxnId = parsed.bankTxnId;
      paymentMode = parsed.paymentMode;
      statusMessage = parsed.statusMessage;
      dateTime = parsed.dateTime;
      customerEmail = parsed.customerEmail;
      customerMobile = parsed.customerMobile;
    }
    // Handle JSON format (from responseHandler)
    else if (paymentData.paymentMethod || paymentData.merchantTxnId) {
      txnId = paymentData.merchantTxnId ||
        paymentData.txnId ||
        (paymentData.paymentMethod && paymentData.paymentMethod.paymentTransaction ?
          paymentData.paymentMethod.paymentTransaction.identifier : '');

      statusCode = paymentData.statusCode ||
        (paymentData.paymentMethod && paymentData.paymentMethod.paymentTransaction ?
          paymentData.paymentMethod.paymentMethod.paymentTransaction.statusCode : 'Unknown');

      status = paymentData.status || statusCode;

      if (paymentData.paymentMethod && paymentData.paymentMethod.paymentTransaction) {
        const txn = paymentData.paymentMethod.paymentTransaction;
        amount = txn.amount || '';
        bankTxnId = txn.identifier || '';
        statusMessage = txn.statusMessage || '';
        dateTime = txn.dateTime || new Date().toISOString();
      }

      if (paymentData.paymentMethod) {
        paymentMode = paymentData.paymentMethod.paymentMode || '';
      }
    }
    // Handle direct parameters
    else {
      txnId = paymentData.txnId || paymentData.transactionId || '';
      statusCode = paymentData.statusCode || '';
      status = paymentData.status || '';
      amount = paymentData.amount || '';
      bankTxnId = paymentData.bankTxnId || '';
      paymentMode = paymentData.paymentMode || '';
      statusMessage = paymentData.statusMessage || '';
      dateTime = paymentData.dateTime || '';
      customerEmail = paymentData.customerEmail || '';
      customerMobile = paymentData.customerMobile || '';
    }

    if (!txnId) {
      console.error('❌ Cannot store payment: No transaction ID found');
      return;
    }

    const record: PaymentRecord = {
      transactionId: txnId,
      statusCode: statusCode,
      status: status,
      amount: amount,
      bankTxnId: bankTxnId,
      paymentMode: paymentMode,
      statusMessage: statusMessage,
      dateTime: dateTime || new Date().toISOString(),
      customerEmail: customerEmail,
      customerMobile: customerMobile,
      rawResponse: paymentData,
      storedAt: new Date().toISOString()
    };

    this.payments.set(txnId, record);
    this.savePaymentsToStorage();
    this.paymentStatusSubject.next(record);

    console.log('✅ Payment stored successfully:', record);
  }

  /**
   * Parse Worldline pipe-delimited message
   */
  private parseWorldlineMsg(msg: string): any {
    try {
      const decodedMsg = decodeURIComponent(msg).replace(/\+/g, ' ');
      const parts = decodedMsg.split('|');

      let customerEmail = '';
      let customerMobile = '';

      if (parts[8]) {
        const customerInfo = parts[8];
        const emailMatch = customerInfo.match(/\{email:([^}]+)\}/);
        const mobileMatch = customerInfo.match(/\{mob:([^}]+)\}/);

        if (emailMatch) customerEmail = emailMatch[1];
        if (mobileMatch) customerMobile = mobileMatch[1];
      }

      return {
        statusCode: parts[0] || '',
        status: parts[1] || '',
        statusType: parts[2] || '',
        statusMessage: parts[3] || '',
        txnId: parts[4] || '',
        bankTxnId: parts[5] || '',
        merchantId: parts[6] || '',
        amount: parts[7] || '',
        customerEmail: customerEmail,
        customerMobile: customerMobile,
        dateTime: parts[9] || '',
        paymentMode: parts[10] || 'NA'
      };
    } catch (e) {
      console.error('Error parsing Worldline message:', e);
      return {};
    }
  }

  /**
   * Get payment record by transaction ID
   */
  getPaymentRecord(txnId: string): PaymentRecord | undefined {
    return this.payments.get(txnId);
  }

  /**
   * Verify payment status
   */
  verifyPayment(transactionId: string, expectedStatusCode: string): any {
    const storedPayment = this.payments.get(transactionId);

    if (!storedPayment) {
      return {
        verified: false,
        status: 'Not Found',
        message: 'Payment record not found in local storage'
      };
    }

    const verified = storedPayment.statusCode === expectedStatusCode;

    return {
      verified: verified,
      status: storedPayment.statusCode,
      expectedStatus: expectedStatusCode,
      message: verified ? 'Payment verified successfully' : 'Status code mismatch - possible tampering or data inconsistency',
      details: storedPayment
    };
  }

  /**
   * Get all payment records
   */
  getAllPayments(): PaymentRecord[] {
    return Array.from(this.payments.values());
  }

  /**
   * Get recent payments (last N)
   */
  getRecentPayments(limit: number = 10): PaymentRecord[] {
    const allPayments = this.getAllPayments();

    // Sort by storedAt timestamp (newest first)
    allPayments.sort((a, b) => {
      return new Date(b.storedAt).getTime() - new Date(a.storedAt).getTime();
    });

    return allPayments.slice(0, limit);
  }

  /**
   * Get payments by status
   */
  getPaymentsByStatus(statusCode: string): PaymentRecord[] {
    return this.getAllPayments().filter(p => p.statusCode === statusCode);
  }

  /**
   * Clear all payment records
   */
  clearAllPayments(): void {
    this.payments.clear();
    localStorage.removeItem('payment_records');
    console.log('🗑️ All payment records cleared');
  }

  /**
   * Delete specific payment record
   */
  deletePayment(txnId: string): boolean {
    const deleted = this.payments.delete(txnId);
    if (deleted) {
      this.savePaymentsToStorage();
      console.log('🗑️ Payment deleted:', txnId);
    }
    return deleted;
  }

  /**
   * Get payment status observable
   */
  getPaymentStatusObservable(): Observable<PaymentRecord | null> {
    return this.paymentStatusSubject.asObservable();
  }

  /**
   * Submit feedback to remote API
   */
  submitFeedback(feedbackData: any): Observable<any> {
    const payload = Array.isArray(feedbackData) ? feedbackData : [feedbackData];
    const rawBody = JSON.stringify(payload);

    console.log('📩 Submitting feedback:', rawBody);

    return this._http.post(this.feedbackApiUrl, rawBody, this.options)
      .map((response: Response) => response.text())
      .catch(this.handleError);
  }

  /**
   * Submit enquiry to remote API
   */
  submitEnquiry(enquiryData: any): Observable<any> {
    const payload = Array.isArray(enquiryData) ? enquiryData : [enquiryData];
    const rawBody = JSON.stringify(payload);

    console.log('📩 Submitting enquiry:', rawBody);

    return this._http.post(this.enquiryApiUrl, rawBody, this.options)
      .map((response: Response) => response.text())
      .catch(this.handleError);
  }

  /**
   * Generate payment receipt data
   */
  generateReceiptData(txnId: string): any {
    const payment = this.payments.get(txnId);

    if (!payment) {
      throw new Error('Payment record not found for transaction ID: ' + txnId);
    }

    return {
      transactionId: payment.transactionId,
      amount: payment.amount || '0.00',
      statusCode: payment.statusCode,
      status: payment.status,
      statusMessage: payment.statusMessage || 'N/A',
      paymentMode: payment.paymentMode || 'N/A',
      bankTxnId: payment.bankTxnId || 'N/A',
      dateTime: payment.dateTime || new Date().toISOString(),
      customerEmail: payment.customerEmail || 'N/A',
      customerMobile: payment.customerMobile || 'N/A',
      receiptGeneratedAt: new Date().toISOString()
    };
  }

  /**
   * Check if payment is successful
   */
  isPaymentSuccessful(txnId: string): boolean {
    const payment = this.payments.get(txnId);
    return payment ? payment.statusCode === '0300' : false;
  }

  /**
   * Get payment statistics
   */
  getPaymentStats(): any {
    const allPayments = this.getAllPayments();

    const stats = {
      total: allPayments.length,
      successful: 0,
      failed: 0,
      pending: 0,
      aborted: 0,
      totalAmount: 0
    };

    allPayments.forEach(payment => {
      const amount = parseFloat(payment.amount || '0');
      stats.totalAmount += amount;

      switch (payment.statusCode) {
        case '0300':
          stats.successful++;
          break;
        case '0399':
          stats.failed++;
          break;
        case '0398':
        case '0396':
          stats.pending++;
          break;
        case '0392':
          stats.aborted++;
          break;
      }
    });

    return stats;
  }

  /**
   * Error handler
   */
  private handleError(error: any): Observable<any> {
    console.error('API Error:', error);
    const errorMessage = error.message || 'Server error';
    return Observable.throw(errorMessage);
  }

  /**
   * Export payments as JSON
   */
  exportPaymentsAsJSON(): string {
    const allPayments = this.getAllPayments();
    return JSON.stringify(allPayments, null, 2);
  }

  /**
   * Import payments from JSON
   */
  importPaymentsFromJSON(jsonData: string): boolean {
    try {
      const payments: PaymentRecord[] = JSON.parse(jsonData);

      payments.forEach(payment => {
        if (payment.transactionId) {
          this.payments.set(payment.transactionId, payment);
        }
      });

      this.savePaymentsToStorage();
      console.log('✅ Imported ' + payments.length + ' payments');
      return true;
    } catch (e) {
      console.error('❌ Error importing payments:', e);
      return false;
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/login/services/payment.service.ts